import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, ChevronLeft, Crown, Check, Users, MessageCircle, Calendar, ShieldCheck, Eye, Sparkles, Building, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// GoCardless QR Code
const GOCARDLESS_QR_URL = "https://customer-assets-lqy194kg.emergentagent.net/job_4791deb4-db22-4895-9e0a-21aa9f309cff/artifacts/e4uvguwj_GoCardless-QRCode.png";

const Subscription = () => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      // Start GoCardless checkout via API
      const response = await axios.post(`${API}/payments/gocardless/start`, {
        customer_name: user?.name || user?.parent_name || "KiddiPlay User",
        customer_email: user?.email || "",
        plan_name: "KiddiPlay Premium",
        amount_pence: 699
      });
      
      // Redirect to GoCardless authorization page
      window.location.href = response.data.authorisation_url;
    } catch (error) {
      console.error("GoCardless error:", error);
      toast.error("Failed to start Direct Debit setup. Please try again.");
      setLoading(false);
    }
  };

  if (user?.is_premium) {
    return (
      <div className="min-h-screen bg-[#FFF9F5]" data-testid="subscription-page">
        <header className="sticky top-0 z-40 glass border-b border-slate-100">
          <div className="max-w-lg mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/dashboard" className="text-slate-600 hover:text-slate-800">
                <ChevronLeft className="w-6 h-6" />
              </Link>
              <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Subscription
              </h1>
              <div className="w-6" />
            </div>
          </div>
        </header>

        <main className="max-w-lg mx-auto px-6 py-12 text-center">
          <div className="w-24 h-24 rounded-full premium-gradient flex items-center justify-center mx-auto mb-6">
            <Crown className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            You&apos;re Premium!
          </h2>
          <p className="text-slate-600 mb-8">
            Thank you for supporting KiddiPlay Club. Enjoy all premium features!
          </p>
          <div className="bg-white rounded-3xl p-6 shadow-sm text-left">
            <h3 className="font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>Your Benefits</h3>
            <ul className="space-y-3">
              {[
                { icon: Users, text: "Unlimited matches" },
                { icon: Eye, text: "See profile photos" },
                { icon: MessageCircle, text: "Unlimited messaging" },
                { icon: Calendar, text: "Playdate planner" },
                { icon: ShieldCheck, text: "Priority support" },
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-teal-500" />
                  </div>
                  <span className="text-slate-700">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
          <Link to="/dashboard">
            <Button className="mt-6 bg-teal-500 hover:bg-teal-600 text-white rounded-full px-8" data-testid="back-to-dashboard">
              Back to Dashboard
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9F5]" data-testid="subscription-page">
      {/* Decorative blobs */}
      <div className="blob blob-yellow w-80 h-80 -top-40 -right-40 fixed opacity-40" />
      <div className="blob blob-pink w-64 h-64 bottom-20 -left-32 fixed opacity-40" />

      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-100">
        <div className="max-w-lg mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="text-slate-600 hover:text-slate-800">
              <ChevronLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Go Premium
            </h1>
            <div className="w-6" />
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full premium-gradient flex items-center justify-center mx-auto mb-4 animate-pulse-soft">
            <Crown className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Unlock More Connections
          </h2>
          <p className="text-slate-600">
            Get the most out of KiddiPlay Club with premium features
          </p>
        </div>

        {/* Comparison */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          {/* Free */}
          <div className="bg-white rounded-3xl p-5 shadow-sm">
            <h3 className="font-bold text-slate-800 mb-1" style={{ fontFamily: 'Nunito, sans-serif' }}>Free</h3>
            <p className="text-xs text-purple-600 font-medium mb-4">Try before you subscribe</p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-teal-500" />
                <span className="text-slate-600">Unlimited matches</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-teal-500" />
                <span className="text-slate-600 font-medium">1 FREE meetup</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-teal-500" />
                <span className="text-slate-600">Basic messaging</span>
              </li>
              <li className="flex items-center gap-2 opacity-50">
                <span className="w-4 h-4 text-slate-400">✕</span>
                <span className="text-slate-400">See photos</span>
              </li>
              <li className="flex items-center gap-2 opacity-50">
                <span className="w-4 h-4 text-slate-400">✕</span>
                <span className="text-slate-400">Unlimited meetups</span>
              </li>
            </ul>
          </div>

          {/* Premium */}
          <div className="premium-gradient rounded-3xl p-5 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-white/20 px-3 py-1 rounded-bl-xl text-xs font-medium">
              BEST VALUE
            </div>
            <h3 className="font-bold mb-1" style={{ fontFamily: 'Nunito, sans-serif' }}>Premium</h3>
            <p className="text-xs text-white/80 mb-4">£6.99/month</p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                <span>Unlimited matches</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                <span className="font-medium">Unlimited meetups</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                <span>Unlimited messages</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                <span>See all photos</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                <span>Playdate planner</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Features List */}
        <div className="bg-white rounded-3xl p-6 shadow-sm mb-8">
          <h3 className="font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>Premium Features</h3>
          <ul className="space-y-4">
            {[
              { icon: Users, title: "Unlimited Matches", desc: "Like as many families as you want, every day" },
              { icon: Eye, title: "See Profile Photos", desc: "View verified photos of families before matching" },
              { icon: MessageCircle, title: "Unlimited Messaging", desc: "Chat freely with all your matches" },
              { icon: Calendar, title: "Playdate Planner", desc: "Schedule and coordinate meetups in-app" },
              { icon: Sparkles, title: "AI Activity Ideas", desc: "Get personalized playdate suggestions" },
              { icon: ShieldCheck, title: "Verified Badge", desc: "Stand out with a premium member badge" },
            ].map((feature, i) => (
              <li key={i} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-teal-500" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800">{feature.title}</h4>
                  <p className="text-sm text-slate-500">{feature.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Pricing */}
        <div className="bg-white rounded-3xl p-6 shadow-sm mb-6 text-center">
          <div className="flex items-baseline justify-center gap-1 mb-2">
            <span className="text-4xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>£6.99</span>
            <span className="text-slate-500">/month</span>
          </div>
          <p className="text-sm text-slate-500 mb-1">or £59.99/year (save 29%)</p>
          <p className="text-xs text-purple-600 mb-6">Cancel anytime. No commitment.</p>

          {/* GoCardless QR Code */}
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6 mb-6 border border-teal-100">
            <div className="flex flex-col items-center">
              <img 
                src={GOCARDLESS_QR_URL} 
                alt="Scan to set up Direct Debit" 
                className="w-40 h-40 mb-4 rounded-lg shadow-sm"
              />
              <p className="text-sm text-slate-600 mb-1">Scan to set up Direct Debit</p>
              <p className="text-xs text-slate-500">or click the button below</p>
            </div>
          </div>

          <Button
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-full py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
            data-testid="subscribe-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Redirecting...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Building className="w-5 h-5" />
                Set Up Direct Debit
              </span>
            )}
          </Button>
        </div>

        {/* Trust */}
        <div className="text-center">
          <p className="text-sm text-slate-500 mb-2">
            <ShieldCheck className="w-4 h-4 inline mr-1" />
            Secure payments powered by GoCardless
          </p>
          <p className="text-xs text-slate-400">
            Direct Debit payments are protected by the Direct Debit Guarantee
          </p>
        </div>
      </main>
    </div>
  );
};

export default Subscription;